import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { fetchExams, fetchExamAttempts } from '../../api/adminApi';
import DataList from './DataList';
import DataCard from './DataCard';
import Pagination from '../common/Pagination';

const ITEMS_PER_PAGE = 10;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const ExamsSection = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [nestedData, setNestedData] = useState({});

  const loadExams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchExams(currentPage, ITEMS_PER_PAGE, searchTerm);
      setExams(data.exams || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error loading exams:', err);
      setError('Failed to load exams. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const handleToggle = useCallback(async (exam) => {
    if (expandedId === exam.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(exam.id);

    if (nestedData[exam.id]) return;

    setNestedData((prev) => ({
      ...prev,
      [exam.id]: { loading: true, attempts: [] },
    }));

    try {
      const data = await fetchExamAttempts(exam.id);
      setNestedData((prev) => ({
        ...prev,
        [exam.id]: { loading: false, attempts: data.attempts || [] },
      }));
    } catch (err) {
      console.error(`Error loading attempts for exam ${exam.id}:`, err);
      setNestedData((prev) => ({
        ...prev,
        [exam.id]: { loading: false, attempts: [] },
      }));
    }
  }, [expandedId, nestedData]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedId(null);
  };

  return (
    <div>
      {error && (
        <div className="data-error-banner">
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      <DataList
        searchPlaceholder="Search exams…"
        searchValue={searchTerm}
        onSearch={handleSearch}
        loading={loading}
        empty={!loading && exams.length === 0}
        emptyMessage={searchTerm ? 'No exams match your search.' : 'No exams found.'}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        }
      >
        {exams.map((exam) => {
          const nested = nestedData[exam.id];
          const isExpanded = expandedId === exam.id;

          const subtitleParts = [];
          if (exam.duration) subtitleParts.push(`${exam.duration} min`);
          if (exam.numberOfQuestions) subtitleParts.push(`${exam.numberOfQuestions} questions`);

          return (
            <DataCard
              key={exam.id}
              title={exam.title}
              subtitle={subtitleParts.length ? subtitleParts.join(' · ') : undefined}
              badge={`${exam._count?.attempts ?? 0} Attempts`}
              accentColor="var(--accent-red)"
              expanded={isExpanded}
              onToggle={() => handleToggle(exam)}
            >
              {nested?.loading ? (
                <div className="data-nested-skeleton">
                  <Skeleton count={3} height={36} borderRadius={6} />
                </div>
              ) : nested?.attempts?.length === 0 ? (
                <div className="data-nested-empty">No attempts for this exam.</div>
              ) : (
                <div className="data-nested-table-wrap">
                  <table className="data-nested-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Date</th>
                        <th>Score</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(nested?.attempts || []).map((attempt) => {
                        const stateColor =
                          attempt.state === 'Completed'
                            ? 'var(--accent-green)'
                            : attempt.state === 'In Progress'
                            ? 'var(--accent-orange)'
                            : 'var(--text-muted)';
                        const stateBg =
                          attempt.state === 'Completed'
                            ? 'rgba(132,204,22,0.12)'
                            : attempt.state === 'In Progress'
                            ? 'rgba(249,115,22,0.12)'
                            : 'rgba(100,116,139,0.12)';

                        return (
                          <tr key={attempt.id}>
                            <td>
                              <span className="data-avatar">
                                {`${attempt.student?.firstName?.[0] ?? ''}${attempt.student?.lastName?.[0] ?? ''}`}
                              </span>
                              {attempt.student?.firstName} {attempt.student?.lastName}
                            </td>
                            <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                              {formatDate(attempt.date)}
                            </td>
                            <td>
                              {attempt.percentage != null ? (
                                <div className="data-score-row">
                                  <div className="data-score-track">
                                    <div
                                      className="data-score-fill"
                                      style={{
                                        width: `${attempt.percentage}%`,
                                        backgroundColor:
                                          attempt.percentage >= 70
                                            ? 'var(--accent-green)'
                                            : 'var(--accent-orange)',
                                      }}
                                    />
                                  </div>
                                  <span className="data-score-label">
                                    {attempt.percentage}%
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                              )}
                            </td>
                            <td>
                              <span
                                className="data-status-badge"
                                style={{ color: stateColor, backgroundColor: stateBg }}
                              >
                                {attempt.state || 'Unknown'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </DataCard>
          );
        })}
      </DataList>
    </div>
  );
};

export default ExamsSection;
